import React from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Chip,
  Box,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { SV } from '../../automation/stratovaiTestIds';
import {
  REQUIREMENT_ANALYSIS_ACCORDION_SX,
  REQUIREMENT_ANALYSIS_COPY as C,
  STORY_VISUAL_DETAIL_KEYS,
  titleForStoryVisualDetailKey,
  chipColorForFindingSeverity,
  hasMaterialFindings,
  storyVisualSectionHasDetailContent,
  shouldShowDiagnosticsPanel,
  buildDiagnosticsRows,
  parseMaterialFindingBody,
  displayRequirementSummaryText,
} from './requirementAnalysisUiConfig';

function nonEmptyList(value) {
  return Array.isArray(value) && value.length > 0;
}

function FindingCard({ f }) {
  const structured = parseMaterialFindingBody(f.finding);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Chip size="small" label={f.severity} color={chipColorForFindingSeverity(f.severity)} />
        <Chip
          size="small"
          variant="outlined"
          label={f.category?.replace(/_/g, ' ') || C.findingCategoryFallback}
        />
        {f.owner ? <Chip size="small" variant="outlined" label={f.owner} /> : null}
      </Stack>
      {structured ? (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {structured.title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            {C.findingClarificationsHeading}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25, mb: 1 }}>
            {structured.bullets.map((b, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ display: 'list-item', mb: 0.25 }}
              >
                {b}
              </Typography>
            ))}
          </Box>
        </>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, whiteSpace: 'pre-line' }}>
          {f.finding}
        </Typography>
      )}
      {f.evidence ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
          {C.findingEvidencePrefix} {f.evidence}
        </Typography>
      ) : null}
      {f.why_it_matters ? (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {C.findingWhyPrefix} {f.why_it_matters}
        </Typography>
      ) : null}
      {f.recommendation ? (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
          {C.findingRecommendationPrefix} {f.recommendation}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function RequirementAnalysisSections({ data }) {
  const svc = data?.story_visual_consistency || {};
  const showStoryVisual = storyVisualSectionHasDetailContent(svc);
  const showDiagnostics = shouldShowDiagnosticsPanel(data?.diagnostics);
  const diagRows = showDiagnostics ? buildDiagnosticsRows(data) : [];

  return (
    <Stack spacing={2}>
      <Box data-testid={SV.pk.requirementAnalysisSummaryText}>
        <Typography variant="subtitle1" gutterBottom>
          {C.sectionRequirementSummary}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {displayRequirementSummaryText(data.summary, data)}
        </Typography>
      </Box>

      {hasMaterialFindings(data) ? (
        <Accordion
          defaultExpanded
          sx={REQUIREMENT_ANALYSIS_ACCORDION_SX}
          data-testid={SV.pk.requirementAnalysisFindings}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{C.sectionMaterialFindings}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {data.findings.map((f, i) => (
              <FindingCard key={i} f={f} />
            ))}
          </AccordionDetails>
        </Accordion>
      ) : null}

      {nonEmptyList(data.open_questions) ? (
        <Box data-testid={SV.pk.requirementAnalysisQuestions}>
          <Typography variant="subtitle1" gutterBottom>
            {C.sectionOpenQuestions}
          </Typography>
          <List dense>
            {data.open_questions.map((q, i) => (
              <ListItem key={i} alignItems="flex-start">
                <ListItemText
                  primary={q.question}
                  secondary={
                    <>
                      {q.why_it_matters ? <span>{q.why_it_matters}</span> : null}
                      {q.suggested_owner ? (
                        <Chip size="small" sx={{ mt: 0.5 }} label={q.suggested_owner} />
                      ) : null}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}

      {nonEmptyList(data.suggested_acceptance_criteria) ? (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {C.sectionSuggestedAc}
          </Typography>
          <List dense>
            {data.suggested_acceptance_criteria.map((line, i) => (
              <ListItem key={i}>
                <ListItemText primary={line} />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}

      {nonEmptyList(data.negative_and_edge_scenarios) ? (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {C.sectionNegativeEdge}
          </Typography>
          <List dense>
            {data.negative_and_edge_scenarios.map((line, i) => (
              <ListItem key={i}>
                <ListItemText primary={line} />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}

      {nonEmptyList(data.user_perspective_review) ? (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {C.sectionUserPerspective}
          </Typography>
          <List dense>
            {data.user_perspective_review.map((line, i) => (
              <ListItem key={i}>
                <ListItemText primary={line} />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}

      {showStoryVisual ? (
        <Accordion
          defaultExpanded
          sx={REQUIREMENT_ANALYSIS_ACCORDION_SX}
          data-testid={SV.pk.requirementAnalysisVisual}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{C.sectionStoryVisual}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {STORY_VISUAL_DETAIL_KEYS.map((key) => {
              const items = svc[key];
              if (!nonEmptyList(items)) return null;
              return (
                <Box key={key} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {titleForStoryVisualDetailKey(key)}
                  </Typography>
                  <List dense>
                    {items.map((line, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={line} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ) : null}

      {nonEmptyList(data.assumptions) ? (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {C.sectionAssumptions}
          </Typography>
          <List dense>
            {data.assumptions.map((line, i) => (
              <ListItem key={i}>
                <ListItemText primary={line} />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}

      {showDiagnostics ? (
        <Accordion
          defaultExpanded={false}
          sx={REQUIREMENT_ANALYSIS_ACCORDION_SX}
          data-testid={SV.pk.requirementAnalysisDiagnostics}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{C.sectionDiagnostics}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.75} component="dl" sx={{ m: 0 }}>
              {diagRows.map((row) => (
                <Box key={row.label} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Typography component="dt" variant="body2" sx={{ fontWeight: 600, minWidth: 180 }}>
                    {row.label}
                  </Typography>
                  <Typography component="dd" variant="body2" color="text.secondary" sx={{ m: 0, flex: 1, minWidth: 0 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
